import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty } from 'lodash-es';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { z } from 'zod';
import FormTextarea from '@/components/common/form-textarea';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useFriendInfo from '@/hooks/users/use-friend-info';
import useUpdateFriendRemark from '@/hooks/users/use-update-friend-remark';
import type { RespondedType } from '@/models/response';
import ROUTES from '@/routes';

export default function UpdateUserRemarkRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId: friendId } = useTypedParams(ROUTES.CONTACT.USERS.BY_ID);

  const { friendInfo, isLoading } = useFriendInfo(friendId);
  const { mutateAsync: updateFriendRemark } = useUpdateFriendRemark(friendId);

  const { control, handleSubmit, resetField } = useForm<UpdateRemarkFormData>({
    resolver: zodResolver(updateRemarkFormScheme),
    defaultValues: {
      nickname: '',
    },
  });

  useEffect(() => {
    if (friendInfo?.FriendInfo) {
      const { RemarkName = '', FriendNickName = '' } = friendInfo.FriendInfo;
      if (RemarkName !== FriendNickName) resetField('nickname', { defaultValue: RemarkName });
    }
  }, [friendInfo?.FriendInfo, resetField]);

  useAdjustLayout();

  const submit = async (data: UpdateRemarkFormData) => {
    let result: RespondedType;

    if (data.nickname === friendInfo?.FriendInfo?.RemarkName) {
      result = { Code: 0, Msg: '' };
    } else if (isEmpty(data.nickname) || data.nickname === friendInfo?.FriendInfo?.FriendNickName) {
      result = await updateFriendRemark('');
    } else {
      result = await updateFriendRemark(data.nickname);
    }

    if (result.Code === 0) {
      navigate(-1);
    }
  };

  return (
    <PageLayout
      title={t('user.remark.name')}
      className='pb-safe-offset-4'
      headerConfig={{
        navigateBack: true,
      }}>
      <LoadingLayout promise={isLoading}>
        {() => (
          <form
            onSubmit={handleSubmit(submit)}
            className='mx-5 pt-4 flex flex-1 flex-col justify-between'>
            <label
              htmlFor='nickname'
              className='form-control w-full'>
              <FormTextarea
                disabled={isLoading}
                name='nickname'
                defaultValue=''
                placeholder={friendInfo?.FriendInfo.FriendNickName}
                control={control}
                maxLength={50}
                rows={5}
                helperText={t('fields.messages.max-length', { name: t('user.remark.name'), count: 50 })}
                showClearButton
              />
            </label>

            <ProgressButton
              as='submit'
              isLoading={isLoading}
              className='btn btn-primary'
              label='common.confirm'
            />
          </form>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}

const updateRemarkFormScheme = z.object({
  nickname: z.string().trim().max(50, 'errors.string.max-length|count=50'),
});

type UpdateRemarkFormData = z.infer<typeof updateRemarkFormScheme>;
